'use client';

import { FC, memo } from 'react';

import dayjs from 'dayjs';

import classNames from 'classnames';

import { useFlags } from 'launchdarkly-react-client-sdk';

import { Button, GenExternalLink, GenNavTile } from '@/components';

import { TypeAccountNavTileFields } from '@/core/generated-types';

import {
  isSubEngineSubscriptionActive,
  isSubEngineSubscriptionCancelled,
  isSubEngineSubscriptionPaused,
} from '@/lib/api/subscriptionManager/helpers/subscriptionHelpers';

import {
  SubEngineInstallmentStatusEnum,
  TypeSubEngineSubscription,
  TypeSubEngineSubscriptionsResponse,
} from '@/lib/api/subscriptionManager/types';

import { TypeAccountSettingsPageTranslations } from '@/utils/translations/types/TypeAccountSettingsPageTranslations';

import {
  CLICKED_ITEM_TYPES,
  PAGE_LOCATIONS,
  trackItemClickedEvent,
} from '@/utils/rudderstack';

import NextBoxDetails from './NextBoxDetails';
import { BUTTON_SIZES } from '@/components/ui/Buttons/utils';
import { KeyedMutator } from 'swr';
import { getLocaleInfo } from '@/utils/helpers';

export type ManageERCSubscriptionsTileProps = {
  t: TypeAccountSettingsPageTranslations;
  subscriptions: TypeSubEngineSubscription[];
  tile: TypeAccountNavTileFields;
  locale: string | undefined;
  refetchSubEngineSubscriptions: KeyedMutator<TypeSubEngineSubscriptionsResponse>;
};

type ManageERCSubscriptionProps = {
  subscription: TypeSubEngineSubscription;
};

const ManageSubscriptionsTile: FC<ManageERCSubscriptionsTileProps> = ({
  t,
  subscriptions,
  tile,
  locale,
  refetchSubEngineSubscriptions,
}) => {
  if (!subscriptions || subscriptions.length === 0) return null;

  const ManageSubscription: FC<ManageERCSubscriptionProps> = ({
    subscription,
  }) => {
    const { language } = getLocaleInfo(locale);
    const langRoot = language === 'en' ? '' : `/${language}`;

    const { 'sub-squad-enable-erc-pause-state': cancelSaverEnabled } =
      useFlags();

    const nextUnprocessedItem = subscription.installments.find(
      (box) =>
        box.status === SubEngineInstallmentStatusEnum.SCHEDULED ||
        box.status === SubEngineInstallmentStatusEnum.PAUSED,
    );

    const lastSkippedItem = subscription.installments.findLast(
      (box) => box.status === SubEngineInstallmentStatusEnum.SKIPPED,
    );

    const today = dayjs();

    const targetItem =
      lastSkippedItem && today.isSameOrBefore(lastSkippedItem.scheduled_date)
        ? lastSkippedItem
        : nextUnprocessedItem;

    const isCancelled = isSubEngineSubscriptionCancelled(subscription);

    const isPaused = isSubEngineSubscriptionPaused(subscription);

    const isActive = isSubEngineSubscriptionActive(subscription);

    const hasUpcomingItems = !!((isActive || isPaused) && targetItem);

    const isPausable = subscription.pausable;

    const isSkippable = nextUnprocessedItem?.skippable;

    const linkProps = isCancelled
      ? {
          path: 'reactivate',
          cta: t.reactivateERCSubscriptionCta,
        }
      : {
          path:
            cancelSaverEnabled && !isPaused && (isPausable || isSkippable)
              ? 'skip-cancel'
              : 'cancel',
          cta: t.cancelERCSubscriptionCta,
        };

    const isSkipCancelLinkVisible =
      !cancelSaverEnabled && isActive && nextUnprocessedItem?.skippable;

    const handleModifySkipClick = () => {
      trackItemClickedEvent({
        url: `/account/early-reader-club/${subscription.id}/modify/${nextUnprocessedItem?.id}`,
        type: CLICKED_ITEM_TYPES.BUTTON,
        pageLocation: PAGE_LOCATIONS.MANAGE_ERC_SUBSCRIPTION_TILE,
        cta: 'Modify/Skip',
      });
      // Navigate to the ship date modification page
      window.location.href = `${langRoot}/account/early-reader-club/${subscription.id}/modify/${nextUnprocessedItem?.id}`;
    };

    const handleCancelClick = () => {
      if (isCancelled) return;

      trackItemClickedEvent({
        url: `/account/early-reader-club/${subscription.id}/${linkProps.path}`,
        type: CLICKED_ITEM_TYPES.LINK,
        pageLocation: PAGE_LOCATIONS.MANAGE_ERC_SUBSCRIPTION_TILE,
        cta: linkProps.cta,
      });
    };

    const handleCancelSkipClick = () => {
      trackItemClickedEvent({
        url: `/account/early-reader-club/${subscription.id}/skip-cancel`,
        type: CLICKED_ITEM_TYPES.LINK,
        pageLocation: PAGE_LOCATIONS.MANAGE_ERC_SUBSCRIPTION_TILE,
        cta: t.modifySubscriptionCta,
      });
    };

    return (
      <div className="mb-8 border-b border-grey-midtone pb-8 last:mb-0 last:border-none last:pb-0">
        <p className="mb-4 text-lg md:text-xl font-bold">{t.readersBoxes}</p>

        <div className="grid grid-cols-[88px_1fr] md:grid-cols-[112px_1fr] gap-x-4">
          {hasUpcomingItems ? (
            <NextBoxDetails
              t={t}
              locale={locale}
              nextUnprocessedItem={targetItem}
              isPaused={isPaused}
              refetchSubEngineSubscriptions={refetchSubEngineSubscriptions}
            />
          ) : (
            <p className="col-span-2 text-sm md:text-base">
              {t.noUpcomingInstallmentsMessage}
            </p>
          )}

          <div
            className={classNames('mt-2 grid gap-y-3', {
              'col-start-2': hasUpcomingItems,
              'col-span-2': !hasUpcomingItems,
            })}
          >
            {cancelSaverEnabled &&
              isSubEngineSubscriptionActive(subscription) && (
                <Button
                  classes="justify-self-start"
                  ctaText={t.modifySkipCta}
                  onClick={handleModifySkipClick}
                  size={BUTTON_SIZES.sm}
                />
              )}

            {isSkipCancelLinkVisible && (
              <a
                className="justify-self-start h-8 px-4 text-sm font-bold text-white bg-blue-deep hover:bg-blue-deep-hover rounded flex items-center"
                href={`${langRoot}/account/early-reader-club/${subscription.id}/skip-cancel`}
                target="_self"
                onClick={handleCancelSkipClick}
              >
                {t.modifySubscriptionCta}
              </a>
            )}

            <GenExternalLink
              classes="text-sm md:text-base"
              onClick={handleCancelClick}
              href={`${langRoot}/account/early-reader-club/${subscription.id}/${linkProps.path}`}
              cta={linkProps.cta}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <GenNavTile
      title={tile.title}
      subTitle={tile.subheader}
      description={tile.description}
    >
      {subscriptions.map((subscription) => (
        <ManageSubscription key={subscription.id} subscription={subscription} />
      ))}
    </GenNavTile>
  );
};

export default memo(ManageSubscriptionsTile);
